import { FieldValues } from "react-hook-form";
import { useEventCreateEditContext } from "../EventCreateEditContext/useEventCreateEditContext";
import { UseEventVenueProps, useEventVenue } from "./useEventVenue";
import { Button, Combobox, Spinner, Text } from "@ttc3k/trekker";
import { ControlledInput } from "shared/components/formControl";
import { Box, Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";

type EventVenueProps<T extends FieldValues> = {
  allowCustom: boolean;
  index: number;

  label: string;
} & UseEventVenueProps<T>;

export const EventVenue = <T extends FieldValues>({
  allowCustom,
  index = -1, // -1 means the formFieldRoot is NOT an array
  isEventOrganizer,
  label,
  formFieldRoot,
  setIsEmpty,
}: EventVenueProps<T>) => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.MODAL.EVENT_HOST.LOCATION_SELECTION",
  });

  const { limitHostToOperatorEntityId, isSearchingOperators } =
    useEventCreateEditContext();

  const {
    filteredLocationListCollection,
    formState,
    locationMode,
    comboboxValue,
    comboboxInputValue,
    hasExistingLocation,
    isComboboxOpen,
    lastSearch,
    useSearchMode,
    browsePreloadedFirst,
    handleComboboxInputValueChange,
    handleEventOrganizerComboboxValueChange,
    handleEventLocationsComboboxValueChange,
    handleCustomLocationNameBlur,
    handleSwitchEventOrganizerMode,
    handleSwitchEventLocationsMode,
    handleComboboxOpenChange,
  } = useEventVenue({
    formFieldRoot,
    isEventOrganizer,
    setIsEmpty,
  });

  /**
   * If a user is typing in a custom location name AND they don't click
   * on the `Add new location` option in the modal, we need to set the
   * name that they have entered to be the new location
   */
  const handleLocationNameBlur = () => {
    // If the combobox is not open, the user has slected an item,
    // so we don't need to do anything
    if (!isComboboxOpen) return;

    // Check to see if the last search text is an exact match of one
    // of the items in the collection
    const exactMatch = filteredLocationListCollection.items.find(
      item => item.label.toLowerCase() === lastSearch.toLowerCase(),
    );

    const canAddNewOnBlur =
      !isSearchingOperators &&
      (isEventOrganizer
        ? filteredLocationListCollection.items.length === 0 &&
          !limitHostToOperatorEntityId &&
          allowCustom
        : allowCustom);

    if (
      canAddNewOnBlur &&
      !hasExistingLocation &&
      (!useSearchMode || lastSearch.trim().length >= 2)
    ) {
      // When there are no suggestions (only "Add new location" option),
      // we need to set the value to "NEW"
      if (isEventOrganizer) {
        handleEventOrganizerComboboxValueChange("NEW");
      } else {
        handleEventLocationsComboboxValueChange("NEW", index);
      }
    } else if (filteredLocationListCollection.items.length > 0 && exactMatch) {
      // If there are suggestions AND an exact match is found,
      // we need to set the value to the exact match
      if (isEventOrganizer) {
        handleEventOrganizerComboboxValueChange(exactMatch.value);
      } else {
        handleEventLocationsComboboxValueChange(exactMatch.value, index);
      }
    }
  };

  // Determine if we need to show an error for the Combobox
  // The ControlledInput will show an error automatically.
  // Object-level Zod issues (e.g. superRefine without path) map to `root` on this field.
  const error =
    (formState.errors[formFieldRoot as keyof typeof formState.errors] as
      | {
          root?: { message?: string };
          message?: string;
        }
      | undefined) ?? null;

  const showSearchHint =
    useSearchMode &&
    lastSearch.trim().length < 2 &&
    filteredLocationListCollection.items.length === 0;

  const addNewLabel = `+ ${t(
    isEventOrganizer ? "ADD_NEW_EVENT_ORGANIZER" : "ADD_NEW_LOCATION",
  )}`;

  // Always append "Add new…" at the end of the dropdown when custom
  // entries are allowed. In search mode, wait until the user has typed
  // enough characters (same threshold as search itself).
  const showAddNewLocation =
    !isSearchingOperators &&
    (!useSearchMode || lastSearch.trim().length >= 2) &&
    ((isEventOrganizer &&
      !limitHostToOperatorEntityId &&
      allowCustom) ||
      (!isEventOrganizer && allowCustom));

  return (
    <Flex flexDirection="column" gap="4px">
      {locationMode === "db" ? (
        <Combobox.Root
          label={label}
          collection={filteredLocationListCollection}
          value={comboboxValue}
          inputValue={comboboxInputValue}
          placeholder={t("SEARCH_PLACEHOLDER")}
          tagVisualVariant={"accent.blue"}
          onValueChange={d =>
            isEventOrganizer
              ? handleEventOrganizerComboboxValueChange(d.value[0])
              : handleEventLocationsComboboxValueChange(d.value[0], index)
          }
          onInputValueChange={d => handleComboboxInputValueChange(d.inputValue)}
          onOpenChange={d => handleComboboxOpenChange(d.open)}
          invalid={!!error}
          onBlurCapture={handleLocationNameBlur}
        >
          {isSearchingOperators && !browsePreloadedFirst ? (
            <Flex alignItems="center" gap="100" px="200" py="150">
              <Spinner w="16px" h="16px" />
              <Text visual="smallMedium" color="text.mid">
                {t("SEARCH_LOADING")}
              </Text>
            </Flex>
          ) : showSearchHint ? (
            <Flex px="200" py="150">
              <Text visual="smallMedium" color="text.mid">
                {t("SEARCH_MIN_CHARS_HINT")}
              </Text>
            </Flex>
          ) : (
            <>
              {filteredLocationListCollection.items.map(location => (
                <Combobox.Item
                  key={location.value}
                  item={location}
                  {...location}
                />
              ))}
              {/* Keep the user's own listings on screen while wider results
                  stream in, rather than swapping the list for a spinner. */}
              {isSearchingOperators && browsePreloadedFirst ? (
                <Flex alignItems="center" gap="100" px="200" py="150">
                  <Spinner w="16px" h="16px" />
                  <Text visual="smallMedium" color="text.mid">
                    {t("SEARCH_LOADING")}
                  </Text>
                </Flex>
              ) : null}
              {showAddNewLocation ? (
                <Combobox.Item
                  label={addNewLabel}
                  item={{
                    label: addNewLabel,
                    value: "NEW",
                  }}
                />
              ) : null}
            </>
          )}
        </Combobox.Root>
      ) : (
        <Box position="relative">
          <ControlledInput
            name={`${formFieldRoot}.venue`}
            label={label}
            onBlurCapture={handleCustomLocationNameBlur}
          />
          <Button
            visual="unstyled"
            color={{
              base: "text.accent.blue.mid",
              hover: "text.accent.blue.dark",
              focusVisible: "text.accent.blue.dark",
            }}
            onClick={() =>
              isEventOrganizer
                ? handleSwitchEventOrganizerMode("db")
                : handleSwitchEventLocationsMode("db", index)
            }
            type="button"
            position="absolute"
            right="0"
            top="0"
          >
            {t("SELECT_LOCATION")}
          </Button>
        </Box>
      )}
      {/* This is a hack to show the error message below the Combobox */}
      {locationMode === "db" && error?.root?.message && (
        <Text visual="smallMedium" color="text.error.dark">
          {error.root.message}
        </Text>
      )}
    </Flex>
  );
};
